import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment-timezone";
import { ChevronDown, ChevronUp, Clock } from "lucide-react";
import Section from "./Section";
import { useCompanyTimeFormat } from "../../../../../../../utils/hooks/company";
import { renderSettingsResourceLabel } from "../../../../../../../utils/helpers/settings";

interface Props {
    event: any;
    isOpen: boolean;
    onToggle: (id: string) => void;
}

function EventCard({ event, isOpen, onToggle }: Props) {
    const { t } = useTranslation();
    const firstJob = event.sections?.[0]?.job;
    const sectionCount = event.sections?.length ?? 0;
    const timeFormat = useCompanyTimeFormat();

    const timeRange = useMemo(() => {
        const sections: any[] = event.sections ?? [];
        const start = moment(event.startDate).add(sections[0].start, "seconds");
        const end = moment(event.startDate).add(sections[sections.length - 1].end, "seconds");

        return {
            start: moment.parseZone(start).format(timeFormat),
            end: moment.parseZone(end).format(timeFormat)
        };
    }, [event.sections, event.startDate, timeFormat]);

    return (
        <div className="tw-border tw-rounded-lg tw-overflow-hidden">
            <div
                className="tw-flex tw-items-center tw-justify-between tw-px-3 tw-py-2.5 tw-cursor-pointer hover:tw-bg-muted/20 tw-transition-colors"
                onClick={() => onToggle(event.id)}
            >
                <div className="tw-flex tw-items-center tw-gap-2 tw-min-w-0 tw-flex-1">
                    {timeRange && (
                        <div className="tw-flex tw-items-center tw-gap-1.5 tw-shrink-0">
                            <Clock size={13} className="tw-text-primary" />
                            <span className="tw-text-xs tw-font-medium tw-text-neutral-700">
                                {timeRange.start} – {timeRange.end}
                            </span>
                        </div>
                    )}
                    {firstJob && (
                        <span className="tw-bg-primary/20 tw-px-2 tw-py-0.5 tw-rounded tw-text-xs tw-text-primary tw-font-semibold tw-truncate">
                            {renderSettingsResourceLabel(firstJob)}
                        </span>
                    )}
                </div>
                <div className="tw-flex tw-items-center tw-gap-2 tw-shrink-0 tw-ml-2">
                    {sectionCount > 1 && (
                        <span className="tw-text-xs tw-text-muted-foreground">
                            {sectionCount} {t("sections")}
                        </span>
                    )}
                    {isOpen ? (
                        <ChevronUp
                            size={14}
                            className="tw-text-muted-foreground"
                        />
                    ) : (
                        <ChevronDown
                            size={14}
                            className="tw-text-muted-foreground"
                        />
                    )}
                </div>
            </div>

            {isOpen && (
                <div className="tw-border-t tw-px-3 tw-py-3 tw-space-y-1.5 tw-bg-muted/5">
                    {event.sections?.map((section: any, i: number) => (
                        <Section
                            key={section.id ?? i}
                            section={section}
                            startDate={event.startDate}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

export default EventCard;
